import { useState } from "react"

import { format } from "date-fns"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/contexts/AuthContext"
import {
  useCreateRouteComment,
  useCreateRouteInstance,
  useSamsaraCreateRoute,
  useTrackPodCreateRoute,
} from "@/hooks/useCollections"
import { buildAdhocRouteCode } from "@/lib/adhocRouteCode"
import { formatDayMonthYear } from "@/lib/dateFormats"

import AdhocCodeField from "../AdhocCodeField"

const FIELD_LABEL_CLASS = "text-xs font-medium text-palette-jungleGreen700"

interface NewAdHocRoute {
  code: string
  notes: string
  doubleManned: boolean
}

interface CreateAdHocRouteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDate: Date
  onCreated: (routeId: string | undefined) => void
}

export default function CreateAdHocRouteDialog({
  open,
  onOpenChange,
  selectedDate,
  onCreated,
}: CreateAdHocRouteDialogProps) {
  const { user } = useAuth()
  const createRouteInstance = useCreateRouteInstance()
  const createRouteComment = useCreateRouteComment()
  const samsaraCreateRoute = useSamsaraCreateRoute()
  const trackpodCreateRoute = useTrackPodCreateRoute()

  const create = async ({ code, notes, doubleManned }: NewAdHocRoute): Promise<boolean> => {
    const now = new Date()
    const startTime = new Date(selectedDate)
    startTime.setHours(Math.max(now.getHours(), 8), 0, 0, 0)
    const endTime = new Date(startTime)
    endTime.setHours(startTime.getHours() + 2)

    try {
      const result = await createRouteInstance.mutateAsync({
        code: buildAdhocRouteCode(code),
        date: format(selectedDate, "yyyy-MM-dd"),
        planned_start_time: startTime.toISOString(),
        planned_end_time: endTime.toISOString(),
        depot: "",
        operator: "HIVED",
        is_adhoc: true,
        is_locked: false,
        status: "scheduled",
        delay_minutes: 0,
        total_stops: 0,
        completed_stops: 0,
        // Null rather than 1 when single-manned, so the column means "someone said so", not a
        // default.
        crew_size: doubleManned ? 2 : null,
      })

      if (result?.id) {
        try {
          await samsaraCreateRoute.mutateAsync(result.id)
        } catch (samsaraError) {
          // Don't fail route creation if Samsara push fails
          console.warn("[TimelineView] Failed to push ad-hoc route to Samsara:", samsaraError)
        }

        try {
          await trackpodCreateRoute.mutateAsync(result.id)
        } catch (trackpodError) {
          // Don't fail route creation if Track-POD push fails
          console.warn("[TimelineView] Failed to push ad-hoc route to Track-POD:", trackpodError)
        }
      }

      onCreated(result?.id)

      if (result?.id) {
        try {
          await createRouteComment.mutateAsync({
            route_instance_id: result.id,
            author_name: user?.full_name || "Unknown User",
            content: `[Ad-hoc route created] ${notes.trim()}`,
          })
        } catch (_) {
          // Best-effort: a failed push must not block the rest of the flow.
        }
      }

      onOpenChange(false)
      return true
    } catch (error) {
      console.error("Error creating ad-hoc route:", error)
      toast.error("Error creating route", {
        description: "Failed to create the ad-hoc route. Please try again.",
      })
      return false
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="confirm">
        <CreateAdHocRouteForm
          selectedDate={selectedDate}
          onCancel={() => onOpenChange(false)}
          onCreate={create}
        />
      </DialogContent>
    </Dialog>
  )
}

/** Mounted only while open, so every opening starts empty. */
function CreateAdHocRouteForm({
  selectedDate,
  onCancel,
  onCreate,
}: {
  selectedDate: Date
  onCancel: () => void
  onCreate: (route: NewAdHocRoute) => Promise<boolean>
}) {
  const [notes, setNotes] = useState("")
  const [code, setCode] = useState("")
  const [doubleManned, setDoubleManned] = useState(false)

  const confirm = async () => {
    // Guarded here too: a stray Enter in the textarea bypasses the button.
    if (!code.trim() || !notes.trim()) return
    if (await onCreate({ code, notes, doubleManned })) {
      setNotes("")
      setCode("")
      setDoubleManned(false)
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Ad-hoc Route</DialogTitle>
        <DialogDescription>
          Create a new empty ad-hoc route for {formatDayMonthYear(selectedDate)}. You can add
          stops by dragging on the timeline.
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="grid gap-3">
        <section className="space-y-4 rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-palette-jungleGreen500">
            Route details
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <AdhocCodeField
              id="create-adhoc-code"
              value={code}
              onChange={setCode}
              labelClassName={FIELD_LABEL_CLASS}
              required
            />
            <div className="grid content-start gap-2">
              <Label htmlFor="create-adhoc-double-manned" className={FIELD_LABEL_CLASS}>
                Crew
              </Label>
              <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-palette-jungleGreen200 bg-white px-3 py-2 text-sm">
                <span className="text-palette-jungleGreen900">Double-manned</span>
                <Switch
                  id="create-adhoc-double-manned"
                  checked={doubleManned}
                  onCheckedChange={setDoubleManned}
                />
              </div>
            </div>
          </div>
        </section>
        <section className="space-y-4 rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-palette-jungleGreen500">
            Why
          </h3>
          <div className="grid gap-2">
            <Label htmlFor="create-adhoc-notes" className={FIELD_LABEL_CLASS}>
              Reason for creating this route *
            </Label>
            <Textarea
              id="create-adhoc-notes"
              placeholder="e.g. Extra collections needed for X customer"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px] resize-none border-palette-jungleGreen200 bg-white text-sm"
              maxLength={500}
            />
            <p className="text-xs text-palette-jungleGreen500">
              Saved as a route comment, so the day can be read back later.
            </p>
          </div>
        </section>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={confirm} disabled={!code.trim() || !notes.trim()} variant="confirm">
          Create Route
        </Button>
      </DialogFooter>
    </>
  )
}
